"use client";

import React, { useState, use, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import NicheTag from '@/components/NicheTag';
import Lightbox from '@/components/Lightbox';
import { portfolioProjects } from '@/data/siteData';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import JsonLd from '@/components/SEO/JsonLd';
import usePageTitle from '@/hooks/usePageTitle';
import { PageHero } from '@/components/sections/PageHero';

export default function CaseStudyPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);
  const project = portfolioProjects.find((p) => p.slug === slug);

  // Combine gallery images for the lightbox
  const allImages = useMemo(() => {
    if (!project) return [];
    if (project.gallery) {
      return project.gallery.images;
    }
    return [];
  }, [project]);

  usePageTitle(project ? `${project.name} — Case Study — Laurel Web Co.` : 'Case Study — Laurel Web Co.');

  if (!project) {
    return (
      <Layout>
        <section className="py-32 text-center" aria-label="Project not found">
          <h1 className="heading-hero text-bloom-charcoal">Project not found</h1>
          <Link 
            href="/portfolio" 
            className="font-sans text-bloom-green-mid mt-4 inline-block hover:underline focus-bloom"
            aria-label="Return to portfolio"
          >
            ← Back to Portfolio
          </Link>
        </section>
      </Layout>
    );
  }

  const related = portfolioProjects.filter((p) => project.relatedProjects.includes(p.slug));

  const caseStudySchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": project.name,
    "description": project.tagline,
    "author": {
      "@type": "Person",
      "name": "Crystal Reyes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Laurel Web Co.",
      "url": "https://laurelweb.co"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://laurelweb.co/portfolio/${project.slug}`
    }
  };

  return (
    <Layout>
      <JsonLd schema={caseStudySchema} />
      
      <PageHero
        ariaLabel="Case study introduction"
        title={project.name}
        subtitle={project.tagline}
        contentClassName="max-w-5xl"
        eyebrow={`Case Study • ${project.orgType}`}
      >
        {/* Horizontal Info Bar */}
        <div className="pt-10 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: 'Role', value: project.role },
              { label: 'Timeline', value: project.timeline },
              { label: 'Stack', value: project.techStack.slice(0, 2).join(', ') },
              { label: 'Compliance', value: project.complianceLevel },
            ].map((m) => (
              <div key={m.label}>
                <dt className="font-sans text-xs uppercase tracking-[0.2em] text-bloom-gold font-bold mb-2">{m.label}</dt>
                <dd className="font-sans text-sm md:text-base font-bold text-white leading-snug">{m.value}</dd>
              </div>
            ))}
          </div>
        </div>
      </PageHero>

      {/* 2. SCREENSHOT GALLERY - HIERARCHICAL GRID */}
      {project.gallery && (
        <section className="bg-bloom-cream py-24" aria-label="Project visual interface">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-6xl mx-auto space-y-12">
              <div className="mb-16">
                <span className="text-xs font-bold tracking-[0.3em] text-bloom-gold uppercase mb-4 block leading-none">Visual Interface</span>
                <h2 className="font-serif text-4xl font-normal text-bloom-charcoal">Design Showcase</h2>
              </div>

              {/* Featured Image */}
              <AspectRatio
                ratio={16 / 9}
                className="rounded-3xl border border-bloom-border overflow-hidden cursor-zoom-in shadow-2xl ring-1 ring-bloom-charcoal/5 relative"
                onClick={() => setActiveImageIndex(0)}
              >
                <Image 
                  src={project.gallery.images[0].src} 
                  alt={project.gallery.images[0].alt} 
                  fill
                  priority
                  sizes="(max-width: 1200px) 100vw, 1152px"
                  className="object-cover" 
                />
              </AspectRatio>

              {/* Supporting Thumbnails */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {project.gallery.images.slice(1, 5).map((img, idx) => (
                  <AspectRatio
                    key={img.alt}
                    ratio={16 / 9}
                    className="rounded-2xl border border-bloom-border overflow-hidden cursor-zoom-in shadow-lg hover:shadow-xl transition-shadow ring-1 ring-bloom-charcoal/5 relative"
                    onClick={() => setActiveImageIndex(idx + 1)}
                  >
                    <Image 
                      src={img.src} 
                      alt={img.alt} 
                      fill 
                      sizes="(max-width: 768px) 50vw, 25vw"
                      className="object-cover" 
                    />
                  </AspectRatio>
                ))}
              </div>
              
              {project.gallery.caption && (
                <p className="font-sans text-xs text-bloom-muted italic mt-8">{project.gallery.caption}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 3. IMPACT METRICS - BIG STANDALONE CARDS */}
      <section className="bg-white py-24 border-b border-bloom-border" aria-label="Key impact metrics">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <span className="font-sans text-xs font-bold tracking-[0.3em] text-bloom-green-mid uppercase mb-3 block">Proof of Impact</span>
              <h2 className="font-serif text-4xl font-normal text-bloom-charcoal">Key Metrics & Results</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {project.impactMetrics.slice(0, 4).map((m) => (
                <div key={m.metric} className="bg-bloom-cream/40 rounded-3xl p-10 border border-bloom-border flex flex-col justify-center text-center group hover:bg-white transition-all hover:shadow-xl hover:-translate-y-1 min-h-[220px]">
                  <dt className="font-sans text-xs text-bloom-muted uppercase tracking-widest mb-4 font-bold">{m.metric}</dt>
                  <dd className="font-serif text-3xl font-black text-bloom-green-deep group-hover:text-bloom-gold transition-colors leading-[1.1]">{m.result}</dd>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. CONDENSED NARRATIVE - CHALLENGE ARRAYS */}
      <section className="bg-white py-32" aria-label="Project narrative">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-24">
              
              {/* Challenge Block */}
              <div className="lg:col-span-5">
                <span className="font-sans text-xs font-bold tracking-[0.3em] text-bloom-green-mid uppercase mb-4 block">The Challenge</span>
                <h3 className="font-serif text-4xl font-normal text-bloom-charcoal mb-8">Navigating the Obstacles</h3>
                <p className="font-sans text-lg text-bloom-muted leading-relaxed mb-12 italic">
                  "{project.hook}"
                </p>
                
                <div className="space-y-8">
                  {project.challenge.map((c, i) => (
                    <div key={i} className="flex gap-8 group">
                      <div className="flex-shrink-0">
                        <span className="font-serif text-5xl font-black text-bloom-gold/20 group-hover:text-bloom-gold/40 transition-colors leading-none">
                          0{i + 1}
                        </span>
                      </div>
                      <div className="pt-2">
                        <p className="font-sans text-base text-bloom-muted leading-relaxed group-hover:text-bloom-charcoal transition-colors">
                          {c}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution Block */}
              <div className="lg:col-span-7">
                <span className="font-sans text-xs font-bold tracking-[0.3em] text-bloom-gold uppercase mb-4 block">The Solution</span>
                <h3 className="font-serif text-4xl font-normal text-bloom-charcoal mb-8">What We Built</h3>
                <p className="font-sans text-lg text-bloom-charcoal mb-12 leading-relaxed">
                  {project.solution}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {project.solutions && project.solutions.map((s, i) => (
                    <div key={i} className="p-8 rounded-3xl border border-bloom-border bg-white shadow-sm hover:shadow-md transition-shadow group">
                      <h4 className="font-serif text-lg font-bold text-bloom-green-deep mb-3 group-hover:text-bloom-gold transition-colors">{s.title}</h4>
                      <p className="font-sans text-[13px] text-bloom-muted leading-relaxed">{s.description}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 5. PRESS CALLOUT - BIG TRUST ANCHOR */}
      {project.pressCallout && (
        <section className="bg-bloom-green-deep py-24 relative overflow-hidden">
          {/* Decorative background quote */}
          <div className="absolute -top-12 -left-8 text-[20rem] font-serif text-white/5 pointer-events-none leading-none">“</div>
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <p className="font-serif italic text-3xl md:text-4xl text-white leading-tight mb-12">
                "{project.pressCallout.quote}"
              </p>
              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
                {project.pressCallout.media.map(media => (
                  <span key={media} className="font-serif text-lg font-bold tracking-widest text-bloom-gold uppercase opacity-80">{media}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute top-0 right-0 w-full h-full bg-[url('/grid.svg')] opacity-5 pointer-events-none" />
        </section>
      )}

      {/* 6. TECHNICAL DETAILS - SPLIT VIEW */}
      <section className="bg-white py-32 border-t border-bloom-border" aria-label="Project analysis">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
              
              {/* Technical Performance */}
              <div>
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-10 h-px bg-bloom-gold" />
                  <h3 className="font-serif text-2xl font-normal text-bloom-charcoal">Technical Performance</h3>
                </div>
                <div className="space-y-4">
                  {project.impactMetrics.filter(m => m.metric.toLowerCase().includes('lighthouse') || m.metric.toLowerCase().includes('accessibility') || m.metric.toLowerCase().includes('compliance')).map((m, i) => (
                    <div key={i} className="flex justify-between items-end border-b border-bloom-border pb-4 group">
                      <dt className="font-sans text-xs text-bloom-muted uppercase tracking-widest font-bold group-hover:text-bloom-green-mid transition-colors">{m.metric}</dt>
                      <dd className="font-sans text-lg font-bold text-bloom-green-deep">{m.result}</dd>
                    </div>
                  ))}
                  <div className="pt-8">
                    <p className="font-sans text-xs text-bloom-gold mb-3 uppercase tracking-[0.2em] font-bold">Comprehensive Stack</p>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map(tech => (
                        <span key={tech} className="bg-bloom-cream px-3 py-1.5 rounded-lg text-xs font-bold text-bloom-green-deep border border-bloom-border">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Growth Outcomes */}
              <div>
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-10 h-px bg-bloom-gold" />
                  <h3 className="font-serif text-2xl font-normal text-bloom-charcoal">Organizational Growth</h3>
                </div>
                <div className="space-y-4">
                  {project.impactMetrics.filter(m => !m.metric.toLowerCase().includes('lighthouse') && !m.metric.toLowerCase().includes('accessibility') && !m.metric.toLowerCase().includes('compliance')).map((m, i) => (
                    <div key={i} className="flex justify-between items-end border-b border-bloom-border pb-4 group">
                      <dt className="font-sans text-xs text-bloom-muted uppercase tracking-widest font-bold group-hover:text-bloom-green-mid transition-colors">{m.metric}</dt>
                      <dd className="font-sans text-lg font-bold text-bloom-green-deep">{m.result}</dd>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {related.length > 0 && (
        <section className="bg-bloom-cream py-24 border-t border-bloom-border" aria-labelledby="related-heading">
          <div className="container mx-auto px-6">
            <h2 id="related-heading" className="font-serif text-3xl font-normal text-bloom-charcoal mb-12 text-center">Related <span className="italic text-bloom-gold">Impact</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {related.map((r) => (
                <Link 
                  key={r.slug} 
                  href={`/portfolio/${r.slug}`} 
                  className="bg-white rounded-3xl p-8 card-hover block focus-bloom group border border-bloom-border"
                  aria-label={`View related project: ${r.name}`}
                >
                  <NicheTag niche={r.orgType} className="mb-4" />
                  <h3 className="font-serif text-2xl font-normal text-bloom-charcoal group-hover:text-bloom-green-deep transition-colors mb-2">{r.name}</h3>
                  <p className="font-sans text-sm text-bloom-muted leading-relaxed line-clamp-2">{r.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. CTA BLOCK - DARK GREEN WITH GOLD BUTTON */}
      <section className="bg-bloom-green-deep py-32 relative overflow-hidden" aria-labelledby="cta-heading">
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 id="cta-heading" className="font-serif text-4xl md:text-5xl font-normal text-white mb-8 leading-tight">Ready to see these results for your organization?</h2>
            <p className="font-sans text-lg text-bloom-cream/80 mb-12 leading-relaxed">
              We specialize in building high-trust, high-performance digital platforms for 501(c)(3) nonprofits and community organizations. Let's talk about yours.
            </p>
            <Link 
              href={`/contact?ref=${project.slug}`} 
              className="inline-flex items-center px-12 py-5 rounded-xl bg-bloom-gold text-bloom-charcoal font-sans font-bold hover:bg-bloom-gold-dark transition-all transform hover:scale-105 shadow-2xl focus-bloom group"
              aria-label="Book a discovery call"
            >
              Start Your Project <ArrowRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5 pointer-events-none" />
      </section>

      {/* Lightbox component stays the same */}
      <AnimatePresence>
        {activeImageIndex !== null && (
          <Lightbox 
            images={allImages} 
            currentIndex={activeImageIndex} 
            onClose={() => setActiveImageIndex(null)} 
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
